import { Message, MessageMetadata, StreamData, UsageMetadata } from '../../../types/chat';
import { Conversation } from '../types';
import { storage } from '../../../lib/storage';
import { notify } from '../../../utils/toast';
import { updateTokenUsage } from '../../../services/api/tokenApi';

interface StreamHandlerContext {
  auth: {
    jwt: string;
    walletAddress: string;
    nonce: string;
    did: string;
  };
  activeConversation: Conversation;
  userMessage: Message;
  tempAssistantMessage: Message;
  messageState: {
    currentContent: string;
    serverMessageId: string | null;
    workflow_steps: Array<{ title: string; timestamp: string; }>;
    updateMessageContent: (content: string, messageId?: string) => void;
    finalizeMessage: (usage: UsageMetadata) => Message;
  };
  setMessages: (fn: (prev: Message[]) => Message[]) => void;
  setConversations: (fn: (prev: Conversation[]) => Conversation[]) => void;
  setCurrentConversation: (conversation: Conversation) => void;
  setHasNewChat: (value: boolean) => void;
  onCreditUpdate?: (credit: number) => void;
}

const EVENT_TITLES: Record<string, string> = {
  workflow_started: 'Starting workflow',
  node_started: 'Starting task',
  node_finished: 'Task completed',
  iteration_started: 'Starting iteration',
  iteration_next: 'Next iteration',
  iteration_completed: 'Iterations completed',
  workflow_finished: 'Workflow completed',
  message: 'Generating response',
  agent_log: 'Agent Thinking Step',
  message_end: 'Response completed'
};

const getEventTitle = (event: string, data?: any): string => {
  let baseTitle: string;
  if(event && data && event === 'agent_log'){
    baseTitle = data?.label || 'Agent Thinking Step';
    baseTitle = baseTitle.replaceAll('CALL','Algorand MCP Tool Call-->')
    baseTitle = baseTitle.replaceAll('ROUND','Agent Thinking Round:')
    if(baseTitle.includes('Thought'))baseTitle = 'Agent Thinking';
    if(baseTitle.includes('Final Answer'))baseTitle = 'Agent Final Answer';
    if(baseTitle.includes('Action'))baseTitle = 'Agent Action';
    if(baseTitle.includes('Observation'))baseTitle = 'Agent Observation';
    if(baseTitle.includes('Finish'))baseTitle = 'Agent Finish';
    if(baseTitle.includes('Error'))baseTitle = 'Agent Error';
    
  } else if (data?.title) {
    baseTitle = data.title;
  } else {
    baseTitle = EVENT_TITLES[event] || event.split('_').map(word =>
      word.charAt(0).toUpperCase() + word.slice(1)
    ).join(' ');
  }

  // Append event type (Started/Finished) based on the event
  if (event.endsWith('_started')) {
    return `${baseTitle} Started`;
  } else if (event.endsWith('_finished') || event === 'node_finished') {
    return `${baseTitle} Finished`;
  } else if (event === 'iteration_next') {
    return `${baseTitle} Next`;
  } else if (event === 'iteration_completed') {
    return `${baseTitle} Completed`;
  }
  return baseTitle;
};

export const handleStreamData = async (
  data: StreamData,
  context: StreamHandlerContext
): Promise<Conversation> => {
  try {
    const {
      auth,
      activeConversation,
      userMessage,
      tempAssistantMessage,
      messageState,
      setMessages,
      setConversations,
      setCurrentConversation,
      setHasNewChat,
      onCreditUpdate
    } = context;

    // Add workflow step for all events except task_id
    if (data.event !== 'task_id') {
      //console.log('[StreamHandler] Adding workflow step for event:',  data.event);
      messageState.workflow_steps.push({
        title: getEventTitle(data.event, data.data),
        timestamp: new Date().toISOString(),
      });
    }

    // Update message with workflow steps for all workflow-related events
    if ([
      'workflow_started',
      'node_started',
      'node_finished',
      'iteration_started',
      'iteration_next',
      'agent_log',
      'iteration_completed',
      'workflow_finished'
    ].includes(data.event)) {
      const messageId = messageState.serverMessageId || tempAssistantMessage?.id;
      const updatedMessage = {
        ...tempAssistantMessage,
        id: messageId,
        metadata: {
          ...tempAssistantMessage?.metadata,
          workflow_info: {
            steps: messageState.workflow_steps
          }
        }
      };

      // Save workflow info to separate metadata storage
      if (messageId && userMessage?.sender !== 'user') {
        const metadata: MessageMetadata = {
          workflow_info: {
            steps: messageState.workflow_steps
          }
        };
        await storage.saveMessageMetadata(messageId, metadata);
      }
      const prevMsgs = prev =>
        prev.map(msg =>
          msg.id === tempAssistantMessage?.id || msg.id === messageState.serverMessageId
            ? updatedMessage
            : msg
        )
      setMessages(prevMsgs);

      // Store intermediate state for better recovery
      if (!activeConversation.isNew && messageState.serverMessageId) {
        await storage.setMessage(messageState.serverMessageId, updatedMessage);
      }
    }

    // Handle specific events
    switch (data.event) {
      case 'workflow_started': {
        if (data.conversation_id && activeConversation.isNew) {
          const updatedConversation = {
            ...activeConversation,
            id: data.conversation_id,
            title: data.title || userMessage?.content?.slice(0, 128) + (userMessage?.content?.length && userMessage?.content?.length > 128 ? '...' : ''),
            updated_at:  new Date().toISOString(),
            isNew: false,
            isPinned: activeConversation.isPinned || false
          };

          // Update assistant message with message_id if provided
          if (data.message_id ) {
            const messageId = data.message_id;
            messageState.serverMessageId = messageId;
            const updatedMsg = {
              ...tempAssistantMessage,
              id: messageId
            };
            const prevMsgs = prev =>
              prev.map(msg =>
                msg.id === tempAssistantMessage?.id ? updatedMsg : msg
              )
            setMessages(prevMsgs);
          }

          // Store user message first
          await storage.addMessageToConversation(
            auth.walletAddress,
            data.conversation_id,
            userMessage
          );

          // Then store conversation
          if(userMessage?.id)await storage.setConversation(auth.walletAddress, updatedConversation, [userMessage.id]);

          setConversations(prev => [
            updatedConversation,
            ...prev.filter(c => !c.isNew)
          ]);
          setCurrentConversation(updatedConversation);
          return updatedConversation;
        } else if (!activeConversation.isNew) {
          await storage.addMessageToConversation(
            auth.walletAddress,
            activeConversation.id,
            userMessage
          );
        }
        break;
      }

      case 'message': {
        if (data.message_id) {
          const messageId = data.message_id;
          if (!messageState.serverMessageId) {
            messageState.updateMessageContent('', messageId);
          }
          // Ensure we update the message ID in state even if content is empty
          const prevMsgs = prev =>
            prev.map(msg =>
              msg.id === tempAssistantMessage?.id
                ? { ...msg, id: messageId }
                : msg
            )
          setMessages(prevMsgs);
        }
        if (data.answer !== undefined) {
          // Directly use the processed answer from useChatStream
          const updatedContent = messageState.currentContent + data.answer;
          //console.log('[StreamHandler] Updating content:', updatedContent);
          messageState.updateMessageContent(updatedContent);

          // Update UI with intermediate state and store it
          const updatedMessage = {
            ...tempAssistantMessage,
            id: messageState.serverMessageId || tempAssistantMessage?.id,
            content: updatedContent,
            metadata: {
              ...tempAssistantMessage?.metadata,
              workflow_info: {
                steps: messageState.workflow_steps
              },
              // Include partial usage info if available in the data
              usage: data.metadata?.usage || tempAssistantMessage?.metadata?.usage || {
                prompt_tokens: 0,
                completion_tokens: data.answer ? data.answer.length / 4 : 0, // Rough estimate
                total_tokens: 0,
                prompt_price: "0",
                completion_price: "0",
                total_price: "0",
                currency: "USD",
                latency: (Date.now() - new Date(messageState.workflow_steps[0].timestamp).getTime()) / 1000
              }
            }
          };

          // Update total tokens if we have a rough estimate
          if (updatedMessage.metadata.usage && !data.metadata?.usage) {
            updatedMessage.metadata.usage.total_tokens =
              updatedMessage.metadata.usage.prompt_tokens +
              updatedMessage.metadata.usage.completion_tokens;
          }
          const prevMsgs = prev =>
            prev.map(msg =>
              msg.id === tempAssistantMessage?.id || msg.id === messageState.serverMessageId
                ? updatedMessage
                : msg
            )
          setMessages(prevMsgs);

          // Store intermediate state for better recovery
          if (!activeConversation.isNew && messageState.serverMessageId) {
            await storage.setMessage(messageState.serverMessageId, updatedMessage);
          }
        }
        break;
      }

      case 'message_end': {
        if (data.metadata?.usage) {
          try {
            // Update token usage and get new credit amount
            const newCredit = await updateTokenUsage(auth, {
              prompt_tokens: data.metadata.usage.prompt_tokens,
              completion_tokens: data.metadata.usage.completion_tokens,
              total_tokens: data.metadata.usage.total_tokens
            });

            // Notify parent components about credit update
            if (onCreditUpdate) {
              onCreditUpdate(newCredit);
            }

            // Create final message with all metadata
            const finalMessage = {
              ...messageState.finalizeMessage(data.metadata.usage),
              metadata: {
                ...tempAssistantMessage?.metadata,
                usage: data.metadata.usage,
                workflow_info: {
                  steps: messageState.workflow_steps
                }
              }
            };

            console.log('[StreamHandler] Storing final message:', finalMessage);

            // Save metadata separately
            if (finalMessage.id) {
              const metadata: MessageMetadata = {
                workflow_info: {
                  steps: messageState.workflow_steps
                },
                usage: data.metadata.usage
              };
              storage.saveMessageMetadata(finalMessage.id, metadata);
            }

            // Update message in UI
            const prevMsgs = prev =>
              prev.map(msg =>
                msg.id === tempAssistantMessage?.id || msg.id === messageState.serverMessageId
                  ? finalMessage
                  : msg
              )
            setMessages(prevMsgs);

            // Store the final message state
            if (!activeConversation.isNew && finalMessage?.id) {
              // First store the message itself (without metadata to avoid duplication)
              const messageToStore = {
                ...finalMessage,
                metadata: {} // Clear metadata since we're storing it separately
              };
              await storage.setMessage(finalMessage?.id, messageToStore);

              // Then add it to the conversation
              await storage.addMessageToConversation(
                auth.walletAddress,
                activeConversation.id,
                messageToStore
              );

              // Update conversation's last modified time
              // await storage.updateConversation(
              //   auth.walletAddress,
              //   activeConversation.id,
              //   { updated_at: new Date().toISOString() }
              // );
            }

            // Dispatch custom event for scroll trigger
            window.dispatchEvent(new CustomEvent('message-complete'));

            setHasNewChat(false);
          } catch (error) {
            console.error('Error updating token usage:', error);
            notify.error('Failed to update credits');

            // Even if credit update fails, try to save the message
            const finalMessage = messageState.finalizeMessage(data.metadata.usage);

            // Update message in UI even in error case
            const prevMsgs = prev =>
              prev.map(msg =>
                msg.id === tempAssistantMessage?.id || msg.id === messageState.serverMessageId
                  ? finalMessage
                  : msg
              )
            setMessages(prevMsgs
            );

            if (!activeConversation.isNew && finalMessage?.id) {
              await storage.setMessage(finalMessage?.id, finalMessage);
              await storage.addMessageToConversation(
                auth.walletAddress,
                activeConversation.id,
                finalMessage
              );
            }
          }
        }
        break;
      }
    }

    return activeConversation;
  } catch (error) {
    console.error(`Error handling stream event ${data.event}:`, error);
    notify.error('Error processing response');
    return context.activeConversation;
  }
};
